import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUserReservations, fetchReservationData, Reservation } from '../api/reservationApi';
import { getUnreadCountByReservations, markNotificationAsRead } from '../api/notificationApi';
import Logo from '../componets/Logo';
import Footer from '../componets/Footer';
import { authFetch } from '../utils/AuthFetch';
import { SquarePen, SquareX } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/api';

const statusKoreanMap: Record<Reservation['status'], string> = {
  PENDING: '예약 요청 | 승인 대기 중',
  APPROVED: '예약 승인됨 | 결제 대기 중',
  REJECTED: '예약 거절됨',
  PAYMENT_COMPLETED: '결제 완료 | 보관등록 가능',
  STORED: '보관 중',
  RETURNED: '반납 완료',
  CANCELED: '예약 취소됨',
  FINISHED: '예약 종료',
};

const MyReservations2: React.FC = () => {
  const navigate = useNavigate();
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unreadByReservations, setUnreadByReservations] = useState<{ [key: number]: number }>({});

  // 보관 종료 모달 관련 상태
  const [selectedReservation, setSelectedReservation] = useState<Reservation | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState<'edit' | 'delete' | 'storage' | null>(null);
  const [checklist, setChecklist] = useState({
    item1: false,
    item2: false,
    item3: false,
  });

  useEffect(() => {
    const loadReservations = async () => {
      try {
        const data = await getUserReservations();
        setReservations(data);

        const reservationIds = data.map((r) => r.id);
        const unreadByReservations = await getUnreadCountByReservations(reservationIds);
        setUnreadByReservations(unreadByReservations);
      } catch (err) {
        setError('Failed to load reservations');
      } finally {
        setLoading(false);
      }
    };

    loadReservations();
  }, []);

  const handleReservationClick = async (reservationId: number) => {
    navigate(`/page/ReservationDetail/${reservationId}`);

    if (unreadByReservations[reservationId] > 0) {
      try {
        await markNotificationAsRead(reservationId);
        setUnreadByReservations((prev) => ({ ...prev, [reservationId]: 0 }));
      } catch (err) {
        console.error('Failed to mark as read', err);
      }
    }
  };

  const handleEditClick = (reservationId: number) => {
    setModalType('edit');
    setIsModalOpen(true);
  };

  const handleDeleteClick = (reservationId: number) => {
    setModalType('delete');
    setIsModalOpen(true);
  };

  const handlePaymentClick = (reservation: Reservation) => {
    if (reservation.status === 'APPROVED') {
      navigate(`/checkout/${reservation.id}`);
    } else {
      alert('결제는 승인된 예약만 가능합니다.');
    }
  };

  const handleEndStorage = (reservationId: number) => {
    const reservation = reservations.find((r) => r.id === reservationId);
    if (reservation) {
      setModalType('storage');
      setSelectedReservation(reservation);
      setIsModalOpen(true);
    }
  };

  const handleChecklistChange = (key: keyof typeof checklist) => {
    setChecklist((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleReturnStorage = async () => {
    if (!selectedReservation) return;

    try {
      await fetchReservationData(selectedReservation.id, { status: 'RETURNED' });
      setSelectedReservation({ ...selectedReservation, status: 'RETURNED' });
      setIsModalOpen(false);

      alert('보관 종료가 완료되었습니다.');
      navigate('/spaces');
    } catch (error) {
      console.error('Error registering storage:', error);
      alert('보관 종료 처리 중 오류가 발생했습니다.');
    }
  };

  if (loading) return <div className="min-h-screen bg-gray-50 p-6">Loading...</div>;
  if (error) return <div className="min-h-screen bg-gray-50 p-6 text-red-500">{error}</div>;

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Helmet>
        <title>내 예약 | BIN-GO</title>
        <meta name="description" content="내가 예약한 공간 및 내역을 확인할 수 있습니다." />
        <meta property="og:title" content="내 예약 | BIN-GO" />
        <meta property="og:description" content="내 예약 및 공간 이용 이력 확인." />
      </Helmet>
      <header className="px-4 lg:px-6 py-4">
        <Logo />
        <h2 className="text-2xl font-semibold mb-4 mt-6">내 예약</h2>
      </header>

      <div className="flex-1 px-4 lg:px-6 py-4">
        {reservations.length === 0 ? (
          <p>No reservations found.</p>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {reservations.map((reservation) => {
              const hasUnread = unreadByReservations[reservation.id] > 0;

              return (
                <div
                  key={reservation.id}
                  className={`relative rounded shadow overflow-hidden ${
                    hasUnread ? 'border-2 border-green-500' : 'border'
                  }`}
                >
                  {/* 사진 */}
                  <div
                    className="h-48 w-full bg-gray-200 overflow-hidden cursor-pointer"
                    onClick={() => handleReservationClick(reservation.id)}
                  >
                    {reservation.main_image ? (
                      <img
                        src={reservation.main_image}
                        alt={reservation.space.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                        No Image
                      </div>
                    )}
                  </div>

                  {/* 빨간 점 */}
                  {hasUnread && (
                    <span className="absolute top-2 right-2 w-3 h-3 bg-red-500 rounded-full border-2 border-white" />
                  )}

                  <div className="p-4 flex flex-col space-y-2 relative">
                    <div className="absolute top-4 right-4 flex space-x-2">
                      <SquarePen
                        className="text-gray-600 hover:text-blue-600 cursor-pointer w-5 h-5"
                        onClick={() => handleEditClick(reservation.id)}
                      />
                      <SquareX
                        className="text-red-500 hover:text-red-700 cursor-pointer w-5 h-5"
                        onClick={() => handleDeleteClick(reservation.id)}
                      />
                    </div>

                    <p className="font-semibold text-lg">{reservation.space.title}</p>
                    <p className="text-sm text-gray-500">
                      예약 일: {reservation.start_date} - {reservation.end_date || 'N/A'}
                    </p>
                    <p className="text-sm text-gray-500">
                      상태: {statusKoreanMap[reservation.status] || reservation.status}
                    </p>
                    <p className="text-sm text-gray-500">요금: {reservation.total_price?.toLocaleString() || 'N/A'} (/일)</p>

                    {/* 상태별 버튼 */}
                    <div className="flex space-x-2 pt-2">
                      {reservation.status === 'APPROVED' && (
                        <button
                          onClick={() => handlePaymentClick(reservation)}
                          className="px-3 py-1 rounded bg-green-500 text-white text-sm"
                        >
                          결제 하기
                        </button>
                      )}

                      {reservation.status === 'PAYMENT_COMPLETED' && (
                        <button
                          onClick={() => navigate(`/payment/${reservation.id}`)}
                          className="px-3 py-1 rounded bg-gray-500 text-white text-sm"
                        >
                          결제 정보
                        </button>
                      )}

                      {reservation.status === 'STORED' && (
                        <button
                          onClick={() => handleEndStorage(reservation.id)}
                          className="px-3 py-1 rounded bg-blue-500 text-white text-sm"
                        >
                          보관 종료
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 보관 종료 모달 */}
      {isModalOpen && modalType === 'storage' && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded shadow-lg w-96">
            <h3 className="text-xl font-semibold mb-4">보관 종료 확인</h3>

            <textarea
              readOnly
              className="w-full p-2 border rounded bg-gray-100 text-sm mb-4"
              rows={4}
              value={
                '반납 완료 이후 발견한 물건의 하자 또는 파손에 대해 책임을 묻지 않으니 다시 한번 확인 후 반납 완료 신청 부탁드립니다.'
              }
            />

            <div className="mb-4">
              {['안내사항에 동의합니다.', '물건이 파손되지 않았습니다.', '모든 물건을 반납했습니다.'].map(
                (text, i) => {
                  const key = `item${i + 1}` as keyof typeof checklist;
                  return (
                    <label key={key} className="flex items-center mb-2">
                      <input
                        type="checkbox"
                        checked={checklist[key]}
                        onChange={() => handleChecklistChange(key)}
                        className="mr-2"
                      />
                      {text}
                    </label>
                  );
                }
              )}
            </div>

            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
              >
                취소
              </button>
              <button
                onClick={handleReturnStorage}
                disabled={!checklist.item1 || !checklist.item2 || !checklist.item3}
                className={`px-4 py-2 rounded ${
                  !checklist.item1 || !checklist.item2 || !checklist.item3
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-500 text-white hover:bg-blue-600'
                }`}
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}

      {isModalOpen && modalType === 'edit' && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded shadow-lg w-80 text-center">
            <h3 className="text-xl font-semibold mb-4">예약 수정</h3>
            <p className="mb-6 text-gray-700">not available now</p>
            <button
              onClick={() => setIsModalOpen(false)}
              className="px-4 py-2 rounded bg-gray-300 hover:bg-gray-400"
            >
              확인
            </button>
          </div>
        </div>
      )}

      {isModalOpen && modalType === 'delete' && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded shadow-lg w-80 text-center">
            <h3 className="text-xl font-semibold mb-4 text-red-600">예약 삭제</h3>
            <p className="mb-6 text-gray-700">not available now</p>
            <button
              onClick={() => setIsModalOpen(false)}
              className="px-4 py-2 rounded bg-gray-300 hover:bg-gray-400"
            >
              확인
            </button>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default MyReservations2;
